import axios from 'axios';
import keycloak from '../auth/keycloak';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Exposed so other API clients (e.g. pimApi) can build absolute URLs for
// non-axios uses like <img src> and SSE EventSource.
export const baseURLValue = baseURL;

// Build-time fallbacks for local dev. CRA freezes these into the bundle at
// `npm start` time, so the dev server must be restarted after editing
// frontend/.env. localStorage always wins over the build-time fallback so a
// dev can paste a different key in the UI without rebuilding.
const ENV_API_KEY = process.env.REACT_APP_SYNC_API_KEY || '';
const ENV_USER = process.env.REACT_APP_SYNC_USER || '';

export const getEffectiveApiKey = () =>
  localStorage.getItem('syncApiKey') || ENV_API_KEY || '';
export const getEffectiveUser = () =>
  localStorage.getItem('syncUser') || ENV_USER || '';
export const isApiKeyFromEnv = () =>
  !localStorage.getItem('syncApiKey') && Boolean(ENV_API_KEY);

// Bearer session token from a magic-link sign-in. Kept alongside the shared
// API key rather than replacing it: the key still authorises every pre-existing
// endpoint, while the session is what identifies a real person for approvals.
// It is a token in a header, never a cookie, so the API stays CSRF-immune.
export const SESSION_TOKEN_KEY = 'authSessionToken';
export const SESSION_EVENT = 'auth-session-changed';

export const getSessionToken = () => localStorage.getItem(SESSION_TOKEN_KEY) || '';

export const setSessionToken = (token) => {
  if (token) localStorage.setItem(SESSION_TOKEN_KEY, token);
  else localStorage.removeItem(SESSION_TOKEN_KEY);
  try {
    window.dispatchEvent(new CustomEvent(SESSION_EVENT, { detail: { signedIn: Boolean(token) } }));
  } catch {
    // window/CustomEvent not available (SSR/tests) — silently ignore.
  }
};

export const clearSessionToken = () => setSessionToken('');

const syncApi = axios.create({
  baseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

syncApi.interceptors.request.use(async (config) => {
  const apiKey = getEffectiveApiKey();
  const user = getEffectiveUser();
  const legacySession = getSessionToken();

  if (apiKey) config.headers['X-Sync-API-Key'] = apiKey;
  if (user) config.headers['X-User'] = user;

  // Keycloak is now the primary human identity. Refresh shortly before expiry
  // and attach its access token to every API request. Keep the legacy session
  // fallback during migration so older endpoints continue to work.
  if (keycloak.authenticated) {
    try {
      await keycloak.updateToken(30);
      if (keycloak.token) config.headers.Authorization = `Bearer ${keycloak.token}`;
    } catch (error) {
      console.error('Unable to refresh Keycloak token for API request:', error);
    }
  } else if (legacySession) {
    config.headers.Authorization = `Bearer ${legacySession}`;
  }

  return config;
});

// Surface auth failures globally so the sidebar can highlight the
// credentials box and a top banner can prompt the user. We dispatch on
// 401 (bad key) and 503 (server-side SYNC_API_KEYS not configured).
export const SYNC_AUTH_EVENT = 'sync-api-auth-error';

syncApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    // A 401 while holding a session token means that token is expired or
    // revoked: the backend only rejects when *no* credential resolved. Drop it
    // so the console falls back to key auth instead of retrying a dead session.
    if (status === 401 && getSessionToken()) clearSessionToken();
    if (status === 401 || status === 503) {
      try {
        window.dispatchEvent(
          new CustomEvent(SYNC_AUTH_EVENT, {
            detail: {
              status,
              detail: error?.response?.data?.detail || error?.message || 'auth failed',
              hasKey: Boolean(getEffectiveApiKey()),
              keyFromEnv: isApiKeyFromEnv(),
            },
          }),
        );
      } catch {
        // window/CustomEvent not available (SSR/tests) — silently ignore.
      }
    }
    return Promise.reject(error);
  },
);

export const getConfig = () => syncApi.get('/sync/config').then((r) => r.data);

export const getCompanySettings = () =>
  syncApi.get('/sync/company-settings').then((r) => r.data);

export const updateCompanySettings = (company, payload) =>
  syncApi
    .put(`/sync/company-settings/${encodeURIComponent(company)}`, payload)
    .then((r) => r.data);

// ── Freight delivery zones (ship-to address sync) ──
export const getFreightZoneRates = () =>
  syncApi.get('/sync/freight/zone-rates').then((r) => r.data);

export const updateFreightZoneRate = (country, zone, payload) =>
  syncApi
    .put(
      `/sync/freight/zone-rates/${encodeURIComponent(country)}/${encodeURIComponent(zone)}`,
      payload,
    )
    .then((r) => r.data);

export const updateFreightDefaultZone = (company, payload) =>
  syncApi
    .put(`/sync/freight/default-zone/${encodeURIComponent(company)}`, payload)
    .then((r) => r.data);

export const getFreightPostcodeZones = (country) =>
  syncApi
    .get('/sync/freight/postcode-zones', { params: country ? { country } : {} })
    .then((r) => r.data);

export const createFreightPostcodeZone = (payload) =>
  syncApi.post('/sync/freight/postcode-zones', payload).then((r) => r.data);

export const updateFreightPostcodeZone = (id, payload) =>
  syncApi.put(`/sync/freight/postcode-zones/${id}`, payload).then((r) => r.data);

export const deleteFreightPostcodeZone = (id) =>
  syncApi.delete(`/sync/freight/postcode-zones/${id}`).then((r) => r.data);

export const getFreightOverrides = (params) =>
  syncApi.get('/sync/freight/overrides', { params }).then((r) => r.data);

export const upsertFreightOverride = (payload) =>
  syncApi.put('/sync/freight/overrides', payload).then((r) => r.data);

export const deleteFreightOverride = (company, custNum, addressKey) =>
  syncApi
    .delete('/sync/freight/overrides', {
      params: { company, cust_num: custNum, address_key: addressKey },
    })
    .then((r) => r.data);

export const getFreightCustomerAddresses = (company, custNum) =>
  syncApi
    .get(
      `/sync/freight/customers/${encodeURIComponent(company)}/${encodeURIComponent(custNum)}/addresses`,
    )
    .then((r) => r.data);

export const getDashboard = () => syncApi.get('/sync/dashboard').then((r) => r.data);

export const listDiffs = (params) =>
  syncApi.get('/sync/diffs', { params }).then((r) => r.data);

export const getDiffDetail = (sku, marketCode, environment) =>
  syncApi
    .get(`/sync/diffs/${encodeURIComponent(sku)}`, {
      params: { market_code: marketCode, ...(environment ? { environment } : {}) },
    })
    .then((r) => r.data);

export const diffsCsvUrl = (params) => {
  const search = new URLSearchParams(params).toString();
  return `${baseURL}/sync/diffs.csv${search ? `?${search}` : ''}`;
};

export const listMappings = () => syncApi.get('/sync/mappings').then((r) => r.data);

export const updateMapping = (id, version, payload) =>
  syncApi
    .put(`/sync/mappings/${id}`, payload, { headers: { 'If-Match': String(version) } })
    .then((r) => r.data);

export const previewMapping = (payload) =>
  syncApi.post('/sync/mappings/preview', payload).then((r) => r.data);

export const listValueLookups = () =>
  syncApi.get('/sync/value-lookups').then((r) => r.data);

export const getValueLookup = (name) =>
  syncApi.get(`/sync/value-lookups/${encodeURIComponent(name)}`).then((r) => r.data);

export const createValueLookup = (payload) =>
  syncApi.post('/sync/value-lookups', payload).then((r) => r.data);

export const updateValueLookup = (name, version, payload) =>
  syncApi
    .put(`/sync/value-lookups/${encodeURIComponent(name)}`, payload, {
      headers: { 'If-Match': String(version) },
    })
    .then((r) => r.data);

export const deleteValueLookup = (name) =>
  syncApi
    .delete(`/sync/value-lookups/${encodeURIComponent(name)}`)
    .then((r) => r.data);

export const replaceValueLookupEntries = (name, entries) =>
  syncApi
    .put(`/sync/value-lookups/${encodeURIComponent(name)}/entries`, { entries })
    .then((r) => r.data);

export const previewValueLookup = (name) =>
  syncApi
    .post(`/sync/value-lookups/${encodeURIComponent(name)}/preview`)
    .then((r) => r.data);

export const listOverrides = (params) =>
  syncApi.get('/sync/overrides', { params }).then((r) => r.data);

export const createOverride = (payload) =>
  syncApi.post('/sync/overrides', payload).then((r) => r.data);

export const deleteOverride = (id) =>
  syncApi.delete(`/sync/overrides/${id}`).then((r) => r.data);

export const listJobs = (params) =>
  syncApi.get('/sync/jobs', { params }).then((r) => r.data);

export const getJob = (id) => syncApi.get(`/sync/jobs/${id}`).then((r) => r.data);

export const createJob = (payload) =>
  syncApi.post('/sync/jobs', payload).then((r) => r.data);

export const cancelJob = (id) =>
  syncApi.post(`/sync/jobs/${id}/cancel`).then((r) => r.data);

export const getWorkersHealth = () =>
  syncApi.get('/sync/workers/health').then((r) => r.data);

const TERMINAL_STATUSES = ['succeeded', 'failed', 'cancelled'];

/**
 * Resilient SSE subscription shared by the BigCommerce and Epicor job streams.
 *
 * The native EventSource auto-reconnects after a transient connection drop
 * (network blip, or — common in dev — the backend restarting under
 * `uvicorn --reload`). We deliberately do NOT close on a generic `error`, so
 * those reconnects happen and a long-running job's progress keeps flowing.
 * The server re-emits a fresh snapshot on each (re)connect.
 *
 * We close — and fire `onClose` — only when the stream is genuinely done:
 *   - a terminal `progress` payload arrives (the server returns right after),
 *   - the server sends a named `error` event (e.g. `not_found`, which carries
 *     `data`, unlike a generic transport error), or
 *   - the consumer calls the returned unsubscribe function.
 * After the server's idle `timeout` (it returns and the socket closes) the
 * native reconnect kicks in, so we just let it re-attach.
 */
export const subscribeToJobStream = (url, onProgress, onClose) => {
  let closed = false;
  const es = new EventSource(url);
  const shutdown = (notify) => {
    if (closed) return;
    closed = true;
    es.close();
    if (notify && onClose) onClose();
  };
  es.addEventListener('progress', (ev) => {
    let payload;
    try {
      payload = JSON.parse(ev.data);
    } catch (e) {
      console.warn('SSE parse', e);
      return;
    }
    onProgress(payload);
    if (TERMINAL_STATUSES.includes(payload.status)) shutdown(true);
  });
  es.addEventListener('error', (ev) => {
    // A server-sent `event: error` carries data (e.g. not_found) — give up.
    // A generic transport error has no data — let EventSource auto-reconnect.
    if (ev && ev.data) shutdown(true);
  });
  // `timeout` => server hit its idle window and closed; the job may still be
  // running, so let the following native reconnect re-attach. (No-op here.)
  return () => shutdown(false);
};

export const subscribeToJob = (id, onProgress, onClose) =>
  subscribeToJobStream(`${baseURL}/sync/jobs/${id}/stream`, onProgress, onClose);

export default syncApi;
